import { CardElement, Elements, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import stripeConfig, { resolveStripePublishableKey } from "@/config/stripe.config";
import gallerySubscriptionService from "@/services/gallerySubscription.service";
import UserService from "@/services/user.service";
import ContinueButton from "@/shared-components/buttons/components/ContinueButton";
import PublicFormInput from "@/shared-components/inputs/components/PublicFormInput";
import GenericPopup from "@/shared-components/popups/components/GenericPopup";

import "./styles/GallerySubscriptionPaymentModal.css";

const DEFAULT_EU_COUNTRIES = [
  "AT",
  "BE",
  "BG",
  "HR",
  "CY",
  "CZ",
  "DK",
  "EE",
  "FI",
  "FR",
  "DE",
  "GR",
  "HU",
  "IE",
  "IT",
  "LV",
  "LT",
  "LU",
  "MT",
  "NL",
  "PL",
  "PT",
  "RO",
  "SK",
  "SI",
  "ES",
  "SE",
];

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: "#ffffff",
      fontFamily: "Satoshi, sans-serif",
      fontSize: "16px",
      "::placeholder": {
        color: "rgba(255, 255, 255, 0.55)",
      },
    },
    invalid: {
      color: "#ff9898",
    },
  },
  hidePostalCode: true,
};

function PaymentForm({
  plan,
  seatOnly,
  billingAddress,
  vatId,
  stripeSubscriptionId,
  countryOptions,
  countryLoading,
  normalizedCountry,
  onSuccess,
  onError,
  onPriceResolved,
  onBillingChange,
  onVatIdChange,
  showVatField,
  vatFieldNote,
  pricingBreakdown,
  vatStatusMessage,
  vatStatusType,
  onVatVerifiedChange,
  onVatVerify,
  vatVerifying,
  submitHandlerRef,
})
{
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState(null);

  const handleSubmit = useCallback(async (event, { skipVat = false } = {}) => {
    event.preventDefault();
    if (!billingAddress.address_country) {
      setLocalError("Select a billing country to continue.");
      return;
    }
    if (normalizedCountry === "US") {
      if (!billingAddress.address_zip_code || !billingAddress.address_region) {
        setLocalError("ZIP code and state are required for US addresses.");
        return;
      }
    }
    if (!stripe || !elements)
    {
      setLocalError("Stripe is still loading. Please try again.");
      return;
    }

    setSubmitting(true);
    setLocalError(null);

    try
    {
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        throw new Error("Payment form is not ready yet.");
      }

      const paymentMethodResult = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
        billing_details: {
          address: {
            country: billingAddress.address_country || undefined,
            line1: billingAddress.address_street || undefined,
            city: billingAddress.address_city || undefined,
            postal_code: billingAddress.address_zip_code || undefined,
            state: billingAddress.address_region || undefined,
          },
        },
      });

      if (paymentMethodResult?.error) {
        throw paymentMethodResult.error;
      }

      const paymentMethodId = paymentMethodResult?.paymentMethod?.id ?? null;
      if (!paymentMethodId) {
        throw new Error("Unable to save the payment method.");
      }

      const { response, data } = await gallerySubscriptionService.confirmPaymentMethod({
        paymentMethodId,
        plan,
        billing_address: billingAddress,
        stripe_subscription_id: stripeSubscriptionId || undefined,
        vat_id: skipVat ? undefined : (vatId || undefined),
        seat_only: seatOnly || undefined,
      });

      if (!response?.ok)
      {
        if (response.status === 400 && data?.vat_status) {
          const error = new Error("VAT ID not verified. Please check it or continue without VAT ID.");
          error.code = "VAT_NOT_VERIFIED";
          throw error;
        }
        const message = data?.message ?? data?.error_message ?? "Unable to store payment method.";
        throw new Error(message);
      }
      onVatVerifiedChange?.(Boolean(vatId && !skipVat));

      let finalPayload = data;
      if (response.status === 202 && data?.client_secret && data?.stripe_subscription_id) {
        const paymentResult = await stripe.confirmCardPayment(data.client_secret);
        if (paymentResult?.error) {
          throw paymentResult.error;
        }
        if (paymentResult?.paymentIntent?.status !== "succeeded") {
          throw new Error("Unable to complete the payment.");
        }

        const finalizeResult = await gallerySubscriptionService.confirmPaymentMethod({
          stripe_subscription_id: data.stripe_subscription_id,
        });

        if (!finalizeResult?.response?.ok) {
          const message = finalizeResult?.data?.message ?? "Unable to finalize the subscription.";
          throw new Error(message);
        }
        finalPayload = finalizeResult?.data ?? data;
      }

      if (finalPayload?.price) {
        onPriceResolved?.(finalPayload.price, finalPayload?.plan ?? null);
      }
      onSuccess?.(finalPayload);
    }
    catch (error)
    {
      const message = error?.message ?? "Unable to store payment method.";
      setLocalError(message);
      onError?.(error);
    }
    finally
    {
      setSubmitting(false);
    }
  }, [billingAddress, elements, onError, onPriceResolved, onSuccess, plan, seatOnly, stripe, stripeSubscriptionId, vatId]);

  useEffect(() => {
    if (!submitHandlerRef) {
      return;
    }
    submitHandlerRef.current = ({ skipVat = false } = {}) => {
      const submitEvent = new Event("submit", { cancelable: true });
      handleSubmit(submitEvent, { skipVat });
    };
  }, [handleSubmit, submitHandlerRef]);

  return (
    <form className="gallery-subscription-modal__form" onSubmit={handleSubmit}>
      <div className="gallery-subscription-modal__form-body">
        <div className="gallery-subscription-modal__billing">
          <PublicFormInput
            type="select"
            placeholder="Country"
            element="billing_address_country"
            selectOptions={{
              searchable: true,
              options: countryOptions,
            }}
            value={billingAddress.address_country}
            readOnly={countryLoading}
            onChange={(value) => onBillingChange("address_country", value)}
          />
          <PublicFormInput
            placeholder="Street address"
            element="billing_address_street"
            value={billingAddress.address_street}
            onChange={(event) => onBillingChange("address_street", event.target.value)}
          />
          <PublicFormInput
            placeholder="City"
            element="billing_address_city"
            value={billingAddress.address_city}
            onChange={(event) => onBillingChange("address_city", event.target.value)}
          />
          <PublicFormInput
            placeholder="Region / State"
            element="billing_address_region"
            value={billingAddress.address_region}
            onChange={(event) => onBillingChange("address_region", event.target.value)}
          />
          <PublicFormInput
            placeholder="Postal code"
            element="billing_address_zip_code"
            value={billingAddress.address_zip_code}
            onChange={(event) => onBillingChange("address_zip_code", event.target.value)}
          />
          {showVatField && (
            <>
              <p className="gallery-subscription-modal__vat-note">
                {vatFieldNote}
              </p>
              <PublicFormInput
                placeholder="VAT ID (optional)"
                element="vat_id"
                value={vatId}
                onChange={(event) => onVatIdChange(event.target.value)}
              />
              {vatStatusMessage && (
                <p className={`gallery-subscription-modal__vat-status gallery-subscription-modal__vat-status--${vatStatusType || "info"}`}>
                  {vatStatusMessage}
                </p>
              )}
              <ContinueButton
                type="button"
                className="gallery-subscription-modal__vat-verify"
                disabled={!vatId.trim() || vatVerifying}
                onClick={onVatVerify}
              >
                {vatVerifying ? "Verifying VAT ID..." : "Verify VAT ID"}
              </ContinueButton>
            </>
          )}
        </div>

        <div className="gallery-subscription-modal__payment-field">
          <CardElement
            id="gallery-subscription-payment"
            className="gallery-subscription-modal__payment-element"
            options={CARD_ELEMENT_OPTIONS}
          />
        </div>
      </div>

      <div className="gallery-subscription-modal__sticky-footer">
        {pricingBreakdown && (
          <div className="gallery-subscription-modal__pricing">
            <div className="gallery-subscription-modal__pricing-row">
              <span>Subtotal</span>
              <span>{pricingBreakdown.subtotalLabel}</span>
            </div>
            <div className="gallery-subscription-modal__pricing-row">
              <span>VAT</span>
              <span>{pricingBreakdown.vatLabel}</span>
            </div>
            <div className="gallery-subscription-modal__pricing-row total">
              <span>Total</span>
              <span>{pricingBreakdown.totalLabel}</span>
            </div>
          </div>
        )}

        {localError && (
          <p className="gallery-subscription-modal__error" role="alert">
            {localError}
          </p>
        )}

        <ContinueButton
          type="submit"
          className="gallery-subscription-modal__submit"
          disabled={submitting || !stripe || !elements}
        >
          {seatOnly ? "Save payment method" : "Start subscription and pay now"}
        </ContinueButton>
      </div>
    </form>
  );
}

PaymentForm.propTypes = {
  billingAddress: PropTypes.shape({
    address_country: PropTypes.string,
    address_street: PropTypes.string,
    address_city: PropTypes.string,
    address_zip_code: PropTypes.string,
    address_region: PropTypes.string,
  }).isRequired,
  countryOptions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
  })).isRequired,
  countryLoading: PropTypes.bool.isRequired,
  normalizedCountry: PropTypes.string.isRequired,
  onError: PropTypes.func,
  onPriceResolved: PropTypes.func,
  onSuccess: PropTypes.func,
  onBillingChange: PropTypes.func.isRequired,
  onVatIdChange: PropTypes.func.isRequired,
  onVatVerify: PropTypes.func.isRequired,
  vatVerifying: PropTypes.bool.isRequired,
  vatStatusMessage: PropTypes.string,
  vatStatusType: PropTypes.string,
  onVatVerifiedChange: PropTypes.func,
  plan: PropTypes.string.isRequired,
  seatOnly: PropTypes.bool,
  stripeSubscriptionId: PropTypes.string,
  pricingBreakdown: PropTypes.shape({
    subtotalLabel: PropTypes.string,
    vatLabel: PropTypes.string,
    totalLabel: PropTypes.string,
  }),
  showVatField: PropTypes.bool.isRequired,
  vatFieldNote: PropTypes.string.isRequired,
  submitHandlerRef: PropTypes.shape({ current: PropTypes.func }),
  vatId: PropTypes.string,
};

const DEFAULT_PLAN = "standard";

export default function GallerySubscriptionPaymentModal({
  open,
  onClose,
  onSuccess,
  onPriceResolved,
  stripeSubscriptionId,
  plan = DEFAULT_PLAN,
  priceHint = null,
  seatOnly = false,
})
{
  const [resolvedPlan, setResolvedPlan] = useState(plan);
  const [stripePromise, setStripePromise] = useState(() =>
    stripeConfig.publishableKey ? loadStripe(stripeConfig.publishableKey, { locale: "en" }) : null
  );
  const [error, setError] = useState(null);
  const [priceDetails, setPriceDetails] = useState(null);
  const [configPrice, setConfigPrice] = useState(null);
  const [countries, setCountries] = useState([]);
  const [euCountries, setEuCountries] = useState(DEFAULT_EU_COUNTRIES);
  const [configError, setConfigError] = useState(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [vatVerified, setVatVerified] = useState(false);
  const [vatVerifying, setVatVerifying] = useState(false);
  const [vatStatusMessage, setVatStatusMessage] = useState(null);
  const [vatStatusType, setVatStatusType] = useState(null);
  const [configVatPercentage, setConfigVatPercentage] = useState(0);
  const [configCurrency, setConfigCurrency] = useState("EUR");
  const [hasStoredVatId, setHasStoredVatId] = useState(false);
  const [billingAddress, setBillingAddress] = useState({
    address_country: "",
    address_street: "",
    address_city: "",
    address_zip_code: "",
    address_region: "",
  });
  const [vatId, setVatId] = useState("");
  const [profileAddress, setProfileAddress] = useState(null);
  const submitHandlerRef = useRef(null);

  const normalizedCountry = billingAddress.address_country.trim().toUpperCase();
  const isEuCountry = normalizedCountry && euCountries.includes(normalizedCountry);
  const isNonEuCountry = Boolean(normalizedCountry) && !isEuCountry;
  const isVatEligibleCountry = isEuCountry || normalizedCountry === "GB";
  const vatFieldNote = "A VAT ID helps us apply the correct VAT treatment on your invoices. If you are an EU business, a valid VAT ID may qualify you for reverse charge, meaning VAT can be excluded from the amount charged. If no VAT ID is provided, VAT will be charged based on your billing country rules (for EU businesses without a VAT ID, this defaults to Spanish VAT). In live mode, include your country prefix (e.g. IT12345678901).";
  const showVatField = Boolean(isVatEligibleCountry) && !hasStoredVatId;

  const handleBillingChange = useCallback((key, value) => {
    setBillingAddress((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  const handleVatIdChange = useCallback((value) => {
    setVatId(value);
    setVatVerified(false);
    setVatStatusMessage(null);
    setVatStatusType(null);
  }, []);

  const handleVerifyVatId = useCallback(async () => {
    if (!vatId.trim()) {
      setVatStatusMessage(null);
      setVatStatusType(null);
      return;
    }
    setVatVerifying(true);
    setVatStatusMessage(null);
    setVatStatusType(null);
    try {
      const { response, data } = await gallerySubscriptionService.verifyVatId({
        vat_id: vatId,
        billing_address: billingAddress,
      });
      if (!response?.ok) {
        const message = data?.message ?? "VAT ID could not be verified.";
        setVatStatusMessage(message);
        setVatStatusType("error");
        setVatVerified(false);
        return;
      }
      const verified = Boolean(data?.verified);
      setVatVerified(verified);
      if (!verified) {
        setVatStatusMessage("VAT ID could not be verified. VAT will be charged until it is valid.");
        setVatStatusType("error");
      } else {
        setVatStatusMessage("VAT ID verified. Reverse charge will be applied.");
        setVatStatusType("success");
      }
    }
    catch (error) {
      setVatStatusMessage(error?.message ?? "VAT ID could not be verified.");
      setVatStatusType("error");
      setVatVerified(false);
    }
    finally {
      setVatVerifying(false);
    }
  }, [billingAddress, vatId]);

  useEffect(() => {
    setResolvedPlan(plan);
  }, [plan]);

  const formattedPriceHint = useMemo(() => {
    const source = priceDetails ?? configPrice ?? null;
    if (!source || typeof source !== "object") {
      return priceHint;
    }
    const amount = Number(source.amount ?? source.price ?? source.value ?? NaN);
    const currency = source.currency ?? source.currencyCode ?? source.currency_code ?? configCurrency ?? null;
    const normalizedCurrency = typeof currency === "string" ? currency.trim().toUpperCase() : "";
    const currencyLabel = normalizedCurrency === "USD"
      ? "$"
      : normalizedCurrency === "GBP"
        ? "GBP "
        : normalizedCurrency === "EUR"
          ? "€"
          : normalizedCurrency
            ? `${normalizedCurrency} `
            : "€";
    if (!Number.isFinite(amount) || amount <= 0) {
      return priceHint;
    }
    return `${currencyLabel}${amount.toFixed(2)}/month — cancel anytime`;
  }, [configCurrency, configPrice, priceDetails, priceHint]);

  const pricingBreakdown = useMemo(() => {
    const source = priceDetails ?? configPrice ?? null;
    if (!source || typeof source !== "object") {
      return null;
    }
    const amount = Number(source.amount ?? source.price ?? source.value ?? NaN);
    const total = Number(source.totalWithVat ?? source.total_with_vat ?? source.total ?? NaN);
    const currency = source.currency ?? source.currencyCode ?? source.currency_code ?? configCurrency ?? null;
    const normalizedCurrency = typeof currency === "string" ? currency.trim().toUpperCase() : "";
    const currencyLabel = normalizedCurrency === "USD"
      ? "$"
      : normalizedCurrency === "GBP"
        ? "GBP "
        : normalizedCurrency === "EUR"
          ? "€"
          : normalizedCurrency
            ? `${normalizedCurrency} `
            : "€";
    if (!Number.isFinite(amount) || amount <= 0) {
      return null;
    }
    const vatRate = Number.isFinite(configVatPercentage) ? configVatPercentage : 0;
    const hasCountry = Boolean(billingAddress.address_country);
    const isEuNonEs = isEuCountry && normalizedCountry !== "ES";
    const shouldApplySpanishVat = hasCountry && (normalizedCountry === "ES" || (isEuNonEs && !vatVerified));
    const shouldForceZeroVat = hasCountry && isNonEuCountry;
    const vatAmount = shouldApplySpanishVat
      ? amount * vatRate
      : Number.isFinite(total) && total >= amount
        ? total - amount
        : null;
    const zeroVatLabel = `${currencyLabel}0.00`;
    const vatLabel = !hasCountry
      ? "TBD"
      : shouldForceZeroVat || (isEuNonEs && vatVerified)
        ? zeroVatLabel
        : vatAmount !== null && vatAmount > 0
          ? `${currencyLabel}${vatAmount.toFixed(2)}`
          : zeroVatLabel;
    const totalLabel = !hasCountry
      ? `${currencyLabel}${amount.toFixed(2)}`
      : shouldForceZeroVat
        ? `${currencyLabel}${amount.toFixed(2)}`
        : vatAmount !== null && vatAmount > 0
          ? `${currencyLabel}${(amount + vatAmount).toFixed(2)}`
          : Number.isFinite(total) && total >= amount
            ? `${currencyLabel}${total.toFixed(2)}`
            : `${currencyLabel}${amount.toFixed(2)}`;
    return {
      currencyLabel,
      subtotalLabel: `${currencyLabel}${amount.toFixed(2)}`,
      vatLabel,
      totalLabel,
    };
  }, [billingAddress.address_country, configCurrency, configPrice, configVatPercentage, isEuCountry, isNonEuCountry, normalizedCountry, priceDetails, vatVerified]);

  const taxesHint = useMemo(() => {
    if (!billingAddress.address_country) {
      return "Taxes are calculated at checkout.";
    }

    if (isNonEuCountry) {
      return "No EU VAT is charged. Local taxes may apply based on your location.";
    }

    if (isEuCountry && normalizedCountry !== "ES" && !vatId.trim() && !hasStoredVatId) {
      return "VAT will be charged at the Spanish rate unless you add a valid VAT ID.";
    }

    if (showVatField && vatId.trim() && !vatVerified) {
      return "Click verify to check your VAT ID and apply reverse charge if eligible.";
    }

    return null;
  }, [
    billingAddress.address_country,
    isEuCountry,
    isNonEuCountry,
    hasStoredVatId,
    normalizedCountry,
    showVatField,
    vatId,
    vatVerified,
  ]);

  useEffect(() => {
    if (stripePromise)
    {
      return;
    }
    resolveStripePublishableKey().then((key) => {
      if (key)
      {
        setStripePromise(loadStripe(key, { locale: "en" }));
      }
      else
      {
        setError("Stripe configuration is missing.");
      }
    }).catch(() => {
      setError("Stripe configuration is missing.");
    });
  }, [stripePromise]);

  useEffect(() => {
    let isMounted = true;
    if (!open)
    {
      setError(null);
      setPriceDetails(null);
      return () => {
        isMounted = false;
      };
    }
    setConfigLoading(true);
    setConfigError(null);
    gallerySubscriptionService.getConfig()
      .then(({ response, data }) => {
        if (!isMounted) {
          return;
        }
        if (!response?.ok) {
          setConfigError(data?.message ?? "Unable to load countries.");
          return;
        }
        const options = Array.isArray(data?.countries)
          ? data.countries.map((country) => {
            const label = country?.name ?? "";
            const isoCode = country?.isoCode ?? country?.iso_code ?? country?.code ?? "";
            const normalizedIso = typeof isoCode === "string" ? isoCode.trim().toUpperCase() : "";
            if (!label || normalizedIso.length !== 2) {
              return null;
            }
            return {
              label,
              value: normalizedIso,
            };
          }).filter(Boolean)
          : [];
        setCountries(options);
        if (Array.isArray(data?.vat_rules?.eu_countries)) {
          const normalizedEu = data.vat_rules.eu_countries
            .map((code) => (typeof code === "string" ? code.trim().toUpperCase() : ""))
            .filter((code) => code.length === 2);
          if (normalizedEu.length) {
            setEuCountries(normalizedEu);
          } else {
            setEuCountries(DEFAULT_EU_COUNTRIES);
          }
        }
        const overrides = Array.isArray(data?.planOverrides) ? data.planOverrides : [];
        const now = new Date();
        let effectivePlan = plan;
        for (const override of overrides) {
          const overridePlan = typeof override?.plan === "string" ? override.plan.trim() : "";
          if (!overridePlan) {
            continue;
          }
          const start = override?.start ? new Date(override.start) : null;
          const end = override?.end ? new Date(override.end) : null;
          const afterStart = !start || (Number.isFinite(start.getTime()) && now >= start);
          const beforeEnd = !end || (Number.isFinite(end.getTime()) && now < end);
          if (afterStart && beforeEnd) {
            effectivePlan = overridePlan;
            break;
          }
        }
        setResolvedPlan(effectivePlan);
        const planPrice = data?.plans?.[effectivePlan]?.price ?? data?.plans?.[effectivePlan] ?? null;
        setConfigPrice(planPrice);
        const vatPercentage = Number(data?.vatPercentage ?? data?.vat_percentage ?? 0);
        setConfigVatPercentage(Number.isFinite(vatPercentage) ? vatPercentage : 0);
        if (typeof data?.currency === "string" && data.currency.trim()) {
          setConfigCurrency(data.currency.trim().toUpperCase());
        }
      })
      .catch((configErr) => {
        if (!isMounted) {
          return;
        }
        setConfigError(configErr?.message ?? "Unable to load countries.");
      })
      .finally(() => {
        if (isMounted) {
          setConfigLoading(false);
        }
      });

    UserService.getUserSession()
      .then(({ response, data }) => {
        if (!isMounted) {
          return;
        }
        if (response?.ok && data?.gallery_address) {
          setProfileAddress(data.gallery_address);
        }
        if (response?.ok) {
          const rawVatId = data?.gallery_tax_id ?? data?.user?.gallery_tax_id ?? "";
          const vatIdValue = typeof rawVatId === "string" ? rawVatId.trim() : "";
          const hasVatFlag = Boolean(data?.gallery_tax_enabled ?? data?.user?.gallery_tax_enabled);
          const hasVatId = Boolean(vatIdValue) || hasVatFlag;
          setHasStoredVatId(hasVatId);
          if (hasVatFlag) {
            setVatVerified(true);
          }
        }
      })
      .catch(() => {});

    return () => {
      isMounted = false;
    };
  }, [
    open,
    plan,
  ]);

  useEffect(() => {
    if (!profileAddress) {
      return;
    }
    const hasValues = Object.values(billingAddress).some((value) => value);
    if (hasValues) {
      return;
    }
    const rawCountry = profileAddress.country ?? profileAddress.address_country ?? "";
    const countryCandidate = typeof rawCountry === "string" ? rawCountry.trim() : "";
    const upperCandidate = countryCandidate.toUpperCase();
    const resolvedCountry = upperCandidate.length === 2
      ? upperCandidate
      : countries.find((option) => option.label.toLowerCase() === countryCandidate.toLowerCase())?.value ?? "";
    setBillingAddress({
      address_country: resolvedCountry,
      address_street: profileAddress.street ?? "",
      address_city: profileAddress.city ?? "",
      address_zip_code: profileAddress.zip_code ?? "",
      address_region: profileAddress.region ?? "",
    });
  }, [billingAddress, countries, profileAddress]);

  if (!open)
  {
    return null;
  }

  return (
    <GenericPopup
      open={open}
      onBackgroundClick={onClose}
      className="gallery-subscription-modal__container"
      backgroundClassName="gallery-subscription-modal__overlay"
    >
      <button
        type="button"
        className="gallery-subscription-modal__close"
        aria-label="Close"
        onClick={onClose}
      >
        ×
      </button>
      <header className="gallery-subscription-modal__header">
        <h2 className="gallery-subscription-modal__title">Add payment method</h2>
        <p className="gallery-subscription-modal__subtitle">
          {seatOnly
            ? "Save a card for member seats. We use Stripe to store your details securely."
            : "Save a card for your monthly gallery subscription. We use Stripe to store your details securely."}
        </p>
        {formattedPriceHint && (
          <p className="gallery-subscription-modal__price-hint" aria-live="polite">
            {formattedPriceHint}
          </p>
        )}
        {taxesHint && (
          <p className="gallery-subscription-modal__tax-hint">
            {taxesHint}
          </p>
        )}
        {configError && (
          <p className="gallery-subscription-modal__error" role="alert">
            {configError}
          </p>
        )}
      </header>

      {!stripePromise && !error && (
        <p className="gallery-subscription-modal__helper">
          Preparing secure payment form...
        </p>
      )}
      {error && (
        <p className="gallery-subscription-modal__error" role="alert">
          {error}
        </p>
      )}

      {stripePromise && !error && (
        <Elements stripe={stripePromise}>
          <PaymentForm
            plan={resolvedPlan}
            onSuccess={onSuccess}
            onError={(err) => {
              if (err?.code === "VAT_NOT_VERIFIED") {
                setVatStatusMessage("VAT ID could not be verified. VAT will be charged until it is valid.");
                setVatStatusType("error");
              } else {
                setError(err?.message ?? "Unable to store payment method.");
              }
            }}
            onPriceResolved={onPriceResolved}
            billingAddress={billingAddress}
            vatId={vatId}
            stripeSubscriptionId={stripeSubscriptionId}
            seatOnly={seatOnly}
            countryOptions={countries}
            countryLoading={configLoading}
            normalizedCountry={normalizedCountry}
            onBillingChange={handleBillingChange}
            onVatIdChange={handleVatIdChange}
            pricingBreakdown={pricingBreakdown}
            showVatField={showVatField}
            vatFieldNote={vatFieldNote}
            vatStatusMessage={vatStatusMessage}
            vatStatusType={vatStatusType}
            onVatVerifiedChange={setVatVerified}
            onVatVerify={handleVerifyVatId}
            vatVerifying={vatVerifying}
            submitHandlerRef={submitHandlerRef}
          />
        </Elements>
      )}
    </GenericPopup>
  );
}

GallerySubscriptionPaymentModal.propTypes = {
  onClose: PropTypes.func,
  onPriceResolved: PropTypes.func,
  onSuccess: PropTypes.func,
  open: PropTypes.bool,
  plan: PropTypes.string,
  seatOnly: PropTypes.bool,
  stripeSubscriptionId: PropTypes.string,
};
